/**
 * The public tool.
 *
 * Same engine as the workshop, opposite job. The workshop exists to show where
 * every figure came from; this page exists to answer a question asked by someone
 * who has never run an agent and does not want a lesson. So the evidence is not
 * deleted, it is demoted: what could flip a conclusion stays beside the number,
 * everything else goes behind one disclosure.
 *
 * Two steps, then the answer: pick the job, refine it and search in the same step. The
 * search does real work: the price grid is read live from the public rate card when the
 * reader clicks. The loader therefore reports only what actually happened, including the
 * half it did NOT refresh — the capability notes are a dated published ranking and no
 * button can change that.
 */
import {
  catalogItems, estimateItem,
  type Billing, type CatalogInputs, type CatalogItem, type ItemEstimate, type ItemKind,
  type ModelQuote, type Pick,
} from '../engine/catalog'
import { DOMAINS, scorePoints } from '../engine/capability'
import { fetchPricing, type PricingRefresh } from '../engine/live-pricing'
import { CONTACT, HUMAN_DAYS } from '../engine/config'
import { PLANS, plansFor, type Plan, type Reliability } from '../engine/subscriptions'
import { DEFAULT_INPUTS, shownModifiers } from '../engine/projects'
import type { Library, ModelPrice } from '../engine/types'
import { card, clear, disclosure, el, notesStrip, table } from './dom'
import { t } from './lang'
import { count, currencyRateNote, currencySymbol, duration, factor, fromUsd, isoDay, money, planMoney, tokens, toUsd } from './format'
import { ampleurLabels, automationBenchFr, capSentenceFr, ctaLabel, jobDescription, modelAbout, pickTagsFr, tailleHints } from './wording'
import { currencyPicker, langPicker, need, remember, RATE_KEY, storedRate } from '../boot'

/** A choice, not a measurement: the grid read takes ~90ms, below the threshold
 *  where anything is perceived. 450ms is how long it takes to read the loader's
 *  two short lines — without it the transition reads as a glitch rather than as
 *  work. It applies to the failure path too, which is why it is a floor and not
 *  a delay. */
const LOADER_FLOOR_MS = 450
const FETCH_TIMEOUT_MS = 4000

interface GuideState {
  inputs: CatalogInputs
  /** null until the reader picks one. The workshop opens on a default and shows
   *  an answer nobody asked for; here step 1 is a real question. */
  chosen: string | null
  grid: ModelPrice[]
  /** Une réponse est affichée. Toute modification de la question la remet à faux. */
  answered: boolean
  /** Une seule étape ouverte à la fois : elles sont séquentielles, les garder
   *  toutes ouvertes faisait défiler pour rien. */
  openStep: 1 | 2 | 3
  busy: boolean
  /** Le lecteur a saisi ses propres jours-homme. Tant que c'est faux, le champ suit le
   *  type et la taille choisis : un POC d'API et un produit complet ne demandent pas le
   *  même temps à une équipe non plus (Armand, 21/09/2026). */
  humanDaysOwn: boolean
  refresh: PricingRefresh | null
  failed: boolean
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function reducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function narrow(): boolean {
  return window.matchMedia('(max-width: 46rem)').matches
}

export function renderGuide(library: Library): void {
  const state: GuideState = {
    inputs: {
      ...DEFAULT_INPUTS,
      runsPerMonth: null,
      dailyRateUsd: storedRate(DEFAULT_INPUTS.dailyRateUsd),
      // Per use by default: it is what the page answered before the choice existed.
      billing: 'api',
    },
    chosen: null,
    grid: library.pricing.models,
    answered: false,
    openStep: 1,
    busy: false,
    humanDaysOwn: false,
    refresh: null,
    failed: false,
  }

  const nodes = {
    pitch: need('g-pitch'),
    stamp: need('g-stamp'),
    step1: need('g-step1'),
    step2: need('g-step2'),
    answer: need('g-answer'),
    human: need('g-human'),
    contact: need('g-contact'),
    notes: need('g-notes'),
    live: need('g-live'),
  }

  //  Reconstruit à chaque rendu : les libellés du catalogue changent avec la langue, et
  //  une liste figée au chargement resterait en français après un clic sur EN.
  let items = catalogItems(library)

  /** The refreshed grid lives in the view, never in the library: the workshop
   *  must keep printing "prix du {date}" above prices from that date. */
  function pricedLibrary(): Library {
    return { ...library, pricing: { ...library.pricing, models: state.grid } }
  }

  /**
   * Pin the recommended model before anything is shown.
   *
   * There is no model pivot on this page, so `estimate.costUsd` must not be the
   * default model's — nobody chose it. Every model is quoted whichever one is
   * pinned, so the picks are identical on both passes and one extra pass is
   * enough to reach the fixed point.
   */
  function estimate(): ItemEstimate | null {
    if (!state.chosen) return null
    const lib = pricedLibrary()
    let result = estimateItem({ ...state.inputs, typeId: state.chosen }, lib)
    if (!result) return null
    const picks = result.recommendations?.picks
    const target = picks?.find((pick) => pick.kind === 'value')?.model.id
      ?? picks?.find((pick) => pick.kind === 'cheapest')?.model.id
    if (target && target !== state.inputs.modelId) {
      state.inputs = { ...state.inputs, modelId: target }
      result = estimateItem({ ...state.inputs, typeId: state.chosen }, lib) ?? result
    }
    return result
  }

  function announce(message: string): void {
    nodes.live.textContent = message
  }

  // ---------------------------------------------------------------- masthead

  function drawStamp(): void {
    const picker = currencyPicker(() => { drawStamp(); drawAll() })
    for (const [index, button] of [...picker.children].entries()) {
      const code = index === 0 ? 'EUR' : 'USD'
      const on = (currencySymbol() === '€') === (code === 'EUR')
      button.className = `currency-option${on ? ' on' : ''}`
      button.setAttribute('aria-pressed', String(on))
    }
    // No workshop link here: it was duplicated with the footer, and the client
    // page has no reason to point at the bench in its header.
    // La langue à côté de la devise : deux réglages d'affichage, deux pastilles jumelles.
    // La langue d'abord, la devise dessous : on choisit la langue dans laquelle on lit
    // avant de choisir la monnaie dans laquelle on compte (Armand, 21/09/2026).
    nodes.stamp.replaceChildren(langPicker(() => {
      items = catalogItems(library)
      drawStamp()
      drawAll()
    }), picker)
  }

  function drawMasthead(): void {
    nodes.pitch.textContent = t(
      'Vous avez un projet, ou une tâche qui revient chaque semaine ? Voyez ce que '
      + 'ça coûterait, confié à une IA plutôt qu’à une équipe.',
      'Got a project, or a task that comes back every week? See what it would cost, '
      + 'handed to an AI rather than to a team.')
  }

  // ------------------------------------------------------------------ step 1

  /**
   * A step header that doubles as the control for opening its step.
   *
   * Collapsed, it has to show WHAT WAS CHOSEN — a folded step that only says
   * "01" leaves the reader wondering whether their answer survived — and it has
   * to reopen in one click, or the funnel becomes a trap.
   */
  function stepHead(
    step: 1 | 2,
    title: string,
    note: string | null,
    lock: string | null,
    summary: string | null,
  ): HTMLElement {
    const open = state.openStep === step
    const locked = lock !== null && !state.chosen && step !== 1
    const head = el('div', { class: 'step-head' },
      el('span', { class: 'step-num' }, `0${step}`),
      el('h2', { tabindex: '-1' }, title),
      open && note ? el('p', { class: 'note' }, note) : null,
      locked ? el('p', { class: 'step-lock' }, lock) : null,
      !open && summary ? el('p', { class: 'step-summary' }, summary) : null,
      !open && !locked ? el('span', { class: 'step-edit' }, t('Modifier', 'Change')) : null)

    if (!locked && !open) {
      head.setAttribute('role', 'button')
      head.setAttribute('tabindex', '0')
      head.setAttribute('aria-expanded', 'false')
      const openIt = () => { goTo(step) }
      head.addEventListener('click', openIt)
      head.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openIt() }
      })
    }
    return head
  }

  /** Open one step and fold the others. Redraws every step, never partially:
   *  a half-updated accordion shows two open panels. */
  function goTo(step: 1 | 2 | 3): void {
    state.openStep = step
    drawSteps()
    drawAnswer()
  }

  /** What step 2 is set to, in one line, for the folded header. */
  function refineSummary(): string | null {
    const item = items.find((entry) => entry.id === state.chosen)
    if (!item) return null
    const paying = state.inputs.billing === 'subscription'
      ? t('avec abonnement', 'with a subscription')
      : t('sans abonnement', 'pay as you go')
    if (item.kind === 'automation') return capitalise(paying)
    const size = ampleurLabels()[Math.round(state.inputs.ampleur * 2)] ?? t('Moyen', 'Medium')
    const on = shownModifiers()
      .filter((modifier) => modifier.id === 'visual'
        ? state.inputs.modifiers[modifier.id] !== false
        : state.inputs.modifiers[modifier.id] === true)
      .map((modifier) => modifier.label_fr.toLowerCase())
    const options = on.length > 0 ? ` · ${on.join(' · ')}` : ''
    return t(`Taille ${size.toLowerCase()}${options} · ${paying}`,
      `${size} size${options} · ${paying}`)
  }

  /**
   * One line per job, not one card.
   *
   * Cards were a screen of tiles before the reader had answered anything, and the
   * catalogue keeps growing — seven kinds of project on 20/09/2026, against four.
   * A list holds them in one screen; the examples appear on the line the reader
   * picked, where they answer « is that mine? » instead of shouting at everyone.
   */
  function choice(item: CatalogItem): HTMLElement {
    const selected = item.id === state.chosen
    const node = el('button', {
      type: 'button',
      class: `choice${selected ? ' selected' : ''}`,
      'data-kind': item.kind,
      'aria-pressed': String(selected),
    },
    el('b', {}, item.label_fr),
    // The price carried two facts on the workshop tiles: the amount, and what
    // KIND of amount it was. Only the amount is dropped here — without the unit
    // a per-run price reads as a monthly bill.
    el('span', { class: 'unit' },
      item.kind === 'automation'
        ? t('une exécution', 'one run')
        : t('le projet entier', 'the whole project')),
    // One sentence, and one case to recognise oneself in — on every line, not only on
    // the chosen one: choosing sends the reader straight to step 2, so anything shown on
    // selection arrives after the decision it was meant to inform (Armand, 20/09/2026).
    el('span', { class: 'say' },
      item.description_fr,
      item.example_fr
        ? el('span', { class: 'eg' },
          t(`Par exemple, ${item.example_fr}.`, `For instance, ${item.example_fr}.`))
        : null))
    node.addEventListener('click', () => {
      state.chosen = item.id
      state.inputs = {
        ...state.inputs,
        // A project must not inherit the cadence left over from an automation.
        runsPerMonth: item.kind === 'automation' ? item.runsPerMonth : null,
        // Nor the visual loop of a job that had a screen, when this one has none.
        modifiers: { ...state.inputs.modifiers, visual: item.visualByDefault },
      }
      // Both kinds stop at step 2: an automation has no size to set, but how the
      // reader pays changes its answer as much as a project's.
      state.openStep = 2
      invalidate()
      // Folding step 1 already brings the next one up; on a wide screen an extra
      // scroll only disorients. On a phone the steps are tall enough to need it.
      if (narrow()) {
        requestAnimationFrame(() => {
          nodes.step2.scrollIntoView({
            block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth',
          })
        })
      }
    })
    return node
  }

  //  Construit à chaque rendu : les deux en-têtes changent avec la langue.
  function groups(): [ItemKind, string, string][] {
    return [
      ['project', t('Faire construire quelque chose', 'Have something built'),
        t('Un projet, construit une fois. On chiffre le projet entier, du début à la '
          + 'mise en ligne.',
          'A project, built once. We price the whole thing, from the first line to '
          + 'going live.')],
      ['automation', t('Faire tourner quelque chose, tout seul', 'Have something run itself'),
        t('Une tâche qui revient, exécutée sans vous. On chiffre une exécution — vous '
          + 'multipliez par votre propre cadence.',
          'A task that comes back, run without you. We price one run — you multiply by '
          + 'your own cadence.')],
    ]
  }

  function drawSteps(): void {
    clear(nodes.step1)
    nodes.step1.dataset['state'] = state.chosen ? 'done' : 'active'
    nodes.step1.dataset['open'] = String(state.openStep === 1)
    const item = items.find((entry) => entry.id === state.chosen)
    nodes.step1.appendChild(stepHead(1,
      t('Qu’est-ce que vous voulez faire faire ?', 'What do you want done?'),
      t('Choisissez une case. C’est la seule chose obligatoire.',
        'Pick one. It is the only thing we really need.'), null,
      item ? item.label_fr : null))

    if (state.openStep === 1) {
      const body = el('div', { class: 'step-body' })
      for (const [kind, heading, note] of groups()) {
        const group = items.filter((entry) => entry.kind === kind)
        if (group.length === 0) continue
        body.appendChild(el('div', { class: 'group-head' },
          el('span', { class: 'field-label' }, heading),
          el('span', { class: 'note' }, note)))
        body.appendChild(el('div', { class: 'choices' }, ...group.map(choice)))
      }
      nodes.step1.appendChild(body)
    }
    drawRefine()
  }

  // ------------------------------------------------------------------ step 2

  function drawRefine(): void {
    const item = items.find((entry) => entry.id === state.chosen)
    const locked = !item
    const automation = item?.kind === 'automation'
    clear(nodes.step2)
    nodes.step2.dataset['state'] = locked ? 'locked' : 'active'
    nodes.step2.dataset['open'] = String(state.openStep === 2 && !locked)
    nodes.step2.appendChild(stepHead(2, t('Affinez, si vous voulez', 'Fine-tune, if you like'),
      automation
        ? t('Une tâche automatique n’a pas de taille à régler : reste la façon de payer.',
          'An automated task has no size to set: all that is left is how you pay.')
        : t('Les valeurs par défaut sont celles de nos projets mesurés. Si vous ne '
          + 'savez pas, laissez tel quel.',
        'The defaults are those of the projects we measured. If you are not sure, '
          + 'leave them as they are.'),
      t('Choisissez d’abord une case à l’étape 1.', 'Pick something in step 1 first.'),
      refineSummary()))

    if (state.openStep !== 2 || locked) return

    const body = el('div', { class: 'step-body' })
    if (automation && item) {
      body.appendChild(card('', null, billingChoice(),
        // Collée aux deux cartes de facturation, la phrase se lisait comme leur légende.
        el('p', { class: 'note full spaced' },
          t('On chiffre ', 'We price '), el('b', {}, t('une', 'one')),
          t(' exécution. Avec abonnement, le prix affiché reste le mois entier : c’est ce '
            + 'que vous déboursez, quel que soit le nombre d’exécutions qu’il contient.',
          ' run. With a subscription, the price shown stays the whole month: that is what '
            + 'you pay out, however many runs it holds.'))))
    } else {
      body.appendChild(refineCard())
    }
    // Adjusting has no natural end, so the reader needs a way to say "done" — and that way is
    // the search itself. A separate "Continuer" led to a step holding nothing but the button.
    body.appendChild(launchBlock())
    nodes.step2.appendChild(body)
    drawReceipt()
  }

  function refineCard(): HTMLElement {
    const step = Math.round(state.inputs.ampleur * 2)

    // Épingle le modèle recommandé avant que le reste de l'étape ne s'affiche : sans
    // ça, le premier rendu part du modèle par défaut et le suivant du modèle retenu.
    estimate()

    const cartes = el('div', { class: 'sizes' }, ...ampleurLabels().map((label, index) => {
      const node = el('button', {
        type: 'button',
        class: `size${index === step ? ' selected' : ''}`,
        'aria-pressed': String(index === step),
      },
      el('b', {}, label),
      // Ni prix ni part : la carte dit ce qu'on livre, le prix est le résultat et il
      // s'affiche une fois, plus bas. Trois montants ici en faisaient une grille de
      // tarifs, et « 25 % du projet complet » ne veut rien dire pour qui choisit un
      // périmètre (Armand, 21/09/2026).
      el('span', { class: 'size-hint' }, tailleHints()[index] ?? ''))
      node.addEventListener('click', () => {
        state.inputs = { ...state.inputs, ampleur: index / 2 }
        drawRefine()
        invalidate()
      })
      return node
    }))

    // Only the levers someone stands behind: measured, or declared by the person who
    // builds these projects. A switch that moves nothing teaches the reader that none
    // of the switches do anything.
    const toggles = shownModifiers()
      .map((modifier) => {
        const input = el('input', { type: 'checkbox' }) as HTMLInputElement
        input.checked = modifier.id === 'visual'
          ? state.inputs.modifiers[modifier.id] !== false
          : state.inputs.modifiers[modifier.id] === true
        input.addEventListener('change', () => {
          state.inputs = {
            ...state.inputs,
            modifiers: { ...state.inputs.modifiers, [modifier.id]: input.checked },
          }
          invalidate()
        })
        return el('label', { class: 'toggle' }, input,
          el('span', {}, el('b', {}, modifier.label_fr),
            el('em', {}, modifier.hint_fr)))
      })

    return card('', null,
      el('div', { class: 'field-label' }, t('Taille du projet', 'Size of the project')),
      cartes,
      el('div', { class: 'toggles' }, ...toggles),
      billingChoice())
  }

  /**
   * Per use or per month. Two cards rather than a checkbox: each side needs a line saying
   * what it means, and a reader who has never paid for an API cannot guess it from a label.
   */
  function billingChoice(): HTMLElement {
    const options: [Billing, string, string, string][] = [
      ['api', t('Sans abonnement', 'Pay as you go'),
        t('Vous payez ce que le modèle consomme, au tarif public de l’éditeur.',
          'You pay for what the model consumes, at the vendor’s public rate.'),
        t('prix du travail', 'price of the work')],
      ['subscription', t('Avec abonnement', 'With a subscription'),
        t('Un forfait mensuel fixe. On vous dit lequel, et si le projet tient dans son quota.',
          'A flat monthly plan. We name it, and say whether the work fits its quota.'),
        t('prix d’un mois', 'price of one month')],
    ]
    return el('div', { class: 'billing' },
      el('div', { class: 'field-label' }, t('Comment vous payez l’IA', 'How you pay the AI')),
      el('div', { class: 'sizes' }, ...options.map(([id, label, hint, basis]) => {
        const on = state.inputs.billing === id
        const node = el('button', {
          type: 'button', class: `size${on ? ' selected' : ''}`, 'aria-pressed': String(on),
        },
        el('b', {}, label),
        el('span', { class: 'size-hint' }, hint),
        el('span', { class: 'size-basis' }, basis))
        node.addEventListener('click', () => {
          if (state.inputs.billing === id) return
          state.inputs = { ...state.inputs, billing: id }
          drawRefine()
          invalidate()
        })
        return node
      })))
  }

  /** The recommended model's quote: what the page is actually proposing. */
  function pinnedQuote(current: ItemEstimate): ModelQuote | undefined {
    return current.quotes.find((quote) => quote.model.id === current.model.id)
  }

  /**
   * The one figure that stands for "this job, done by AI". Per use it is the estimate's own
   * cost, as it always was; per month it is the recommended model's plan.
   */
  function headline(current: ItemEstimate): number | null {
    if (state.inputs.billing !== 'subscription') return current.costUsd
    return pinnedQuote(current)?.billedUsd ?? null
  }

  // ------------------------------------------------------------------ launch

  /** The recap and the search button, at the foot of step 2. */
  function launchBlock(): HTMLElement {
    const current = estimate()
    const button = el('button', {
      class: 'primary primary-lg', type: 'button',
      disabled: !state.chosen || state.busy,
    }, ctaLabel(state.answered)) as HTMLButtonElement
    button.addEventListener('click', () => { void search() })

    const launch = el('div', { class: 'launch' },
      el('p', { class: 'launch-recap' },
        ...(current
          ? [t('Vous estimez : ', 'You are pricing: '),
            el('b', {}, jobDescription(current, state.inputs)), '.']
          : [t('Choisissez d’abord ce que vous voulez faire.',
            'First pick what you want done.')])),
      el('div', { class: 'launch-cta' }, button),
      el('p', { class: 'launch-sub' },
        t('Gratuit, sans compte. Les tarifs des modèles sont relevés en direct au '
          + 'moment où vous cliquez.',
        'Free, no account. Model rates are read live, the moment you click.')),
      el('div', { class: 'receipt', id: 'g-receipt' }))
    if (state.busy) launch.setAttribute('aria-busy', 'true')
    return launch
  }

  function loaderLine(
    state_: 'running' | 'done' | 'static' | 'failed',
    ...body: (Node | string)[]
  ): HTMLElement {
    return el('p', {
      class: `loader-line${state_ === 'failed' ? ' warn' : ''}`,
      'data-state': state_,
    }, el('span', {}, ...body))
  }

  /** "relevés le 14/09/2026", or a span when the plans were read on different days. */
  function plansFetched(): string {
    const days = [...new Set(PLANS.flatMap((plan) =>
      plan.fetched_fr.match(/\d{2}\/\d{2}\/\d{4}/g) ?? []))]
      .sort((a, b) => a.split('/').reverse().join('').localeCompare(b.split('/').reverse().join('')))
    if (days.length === 0) return t('non datés', 'undated')
    if (days.length === 1) return t(`relevés le ${days[0]}`, `read on ${days[0]}`)
    return t(`relevés entre le ${days[0]} et le ${days[days.length - 1]}`,
      `read between ${days[0]} and ${days[days.length - 1]}`)
  }

  function snapshotDay(): string {
    return isoDay(library.pricing.snapshot_of)
  }

  function drawReceipt(): void {
    const receipt = document.getElementById('g-receipt')
    if (!receipt) return
    const domain = DOMAINS['coding_agent']
    clear(receipt)

    if (state.busy) {
      receipt.appendChild(el('div', { class: 'loader-bar' }, el('i', {})))
      receipt.appendChild(loaderLine('running',
        t('Tarifs des modèles — relevé en direct…', 'Model rates — reading live…')))
      // Rendered straight into its final state, with a GREY tick: it marks a
      // fact, not a success. A spinning dot beside a still one reads instantly
      // as "that half, no".
      receipt.appendChild(loaderLine('static',
        t('Les notes ', 'The '), el('b', {}, '/100'),
        t(' ne bougent pas : elles viennent d’un classement publié, ',
          ' scores do not move: they come from a published ranking, '),
        el('b', {}, domain.fetched_fr),
        t('. Ce bouton ne va chercher que les prix.',
          '. This button only fetches prices.')))
      return
    }

    // Said first and whatever the rate card did: no button refreshes a plan price.
    if (state.inputs.billing === 'subscription') {
      receipt.appendChild(loaderLine('static',
        t('Les prix d’abonnement ne sont publiés en direct par aucun éditeur : ce sont des '
          + 'relevés datés, ',
        'No vendor publishes subscription prices live: these are dated readings, '),
        el('b', {}, plansFetched()), '.'))
    }

    // Keyed on the GRID, not on the answer: clearing a result does not un-read
    // the rate card, and telling the reader otherwise would be a lie.
    if (!state.refresh && !state.failed) {
      receipt.appendChild(loaderLine('static',
        t('Les tarifs affichés datent du ', 'The rates shown are from '),
        el('b', {}, snapshotDay()),
        t('. Le bouton va chercher ceux d’aujourd’hui.',
          '. The button fetches today’s.')))
      return
    }

    if (state.failed || !state.refresh) {
      receipt.appendChild(loaderLine('failed',
        t('Les tarifs en direct n’ont pas répondu. Le calcul est fait sur la grille du ',
          'The live rates did not answer. The figure is computed on the rate card of '),
        el('b', {}, snapshotDay()),
        t(' — il reste valable, les prix ont juste quelques jours.',
          ' — it still holds, the prices are just a few days old.')))
      return
    }

    const { matched, changed, stale, rejected } = state.refresh
    const total = state.grid.length
    receipt.appendChild(loaderLine('done',
      t('Tarifs relevés à l’instant : ', 'Rates read just now: '),
      el('b', {}, t(`${matched} modèles sur ${total}`, `${matched} of ${total} models`)),
      ...(changed.length > 0
        ? [t(', et ', ', and '),
          el('b', {}, t(`${changed.length} prix ${changed.length > 1 ? 'ont' : 'a'} changé`,
            `${changed.length} price${changed.length > 1 ? 's' : ''} changed`)),
          t(` depuis le relevé du ${snapshotDay()}.`, ` since the reading of ${snapshotDay()}.`)]
        : [t(`, aucun prix n’a changé depuis le relevé du ${snapshotDay()}.`,
          `, no price changed since the reading of ${snapshotDay()}.`)])))

    const kept = [...stale, ...rejected]
    if (kept.length > 0) {
      const many = kept.length > 1
      receipt.appendChild(loaderLine('failed',
        el('b', {}, t(`${kept.length} modèle${many ? 's' : ''}`,
          `${kept.length} model${many ? 's' : ''}`)),
        t(` n’${many ? 'ont' : 'a'} pas été relevé${many ? 's' : ''} (${kept.join(', ')}) : `
          + `${many ? 'ils gardent leur prix' : 'il garde son prix'} du ${snapshotDay()}.`,
        ` ${many ? 'were' : 'was'} not read (${kept.join(', ')}): `
          + `${many ? 'they keep their' : 'it keeps its'} price from ${snapshotDay()}.`)))
    }
  }

  async function search(): Promise<void> {
    if (!state.chosen || state.busy) return
    state.busy = true
    drawSteps()

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
    // fetchPricing swallows its own errors and resolves null, so the floor
    // applies to the failure path too and Promise.all never rejects.
    const [refresh] = await Promise.all([
      fetchPricing(state.grid, { signal: controller.signal }),
      sleep(LOADER_FLOOR_MS),
    ])
    clearTimeout(timer)

    if (refresh) {
      state.grid = refresh.models
      state.refresh = refresh
      state.failed = false
    } else {
      state.failed = true
    }

    const first = !state.answered
    state.busy = false
    state.answered = true
    // The comparison with a human opens already computed, on our own order of magnitude:
    // asked for a figure before seeing one, a reader who has never commissioned this work
    // leaves the field empty and never sees the comparison at all. It stays theirs to
    // change — and tant qu'ils n'ont rien saisi, il suit le travail choisi plutôt que de
    // rester sur les 130 jours du premier.
    if (!state.humanDaysOwn) {
      state.inputs = { ...state.inputs, humanDays: defaultHumanDays() }
    }
    state.openStep = 3
    drawSteps()
    drawAnswer()
    drawHuman()
    drawNotes()

    if (first) {
      nodes.answer.hidden = false
      requestAnimationFrame(() => {
        nodes.answer.querySelector('h2')?.focus({ preventScroll: true })
        nodes.answer.scrollIntoView({
          block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth',
        })
      })
      announce(t('Résultat prêt.', 'Result ready.'))
    } else {
      announce(t('Tarifs mis à jour.', 'Rates updated.'))
    }
  }

  // ------------------------------------------------------------------ answer

  function drawAnswer(): void {
    const current = estimate()
    clear(nodes.answer)
    if (!current) { nodes.answer.hidden = true; return }

    const recommendations = current.recommendations
    const picks = recommendations?.picks ?? []
    const monthly = state.inputs.billing === 'subscription'
    const costs = picks.map((pick) => pick.costUsd)
    // Nothing in selectPicks guarantees the order on a grid that moves, and
    // "entre 12 € et 3 €" is a visible bug.
    const low = costs.length > 0 ? Math.min(...costs) : current.costUsd
    const high = costs.length > 0 ? Math.max(...costs) : current.costUsd

    const change = el('button', { class: 'linkish step-change', type: 'button' },
      t('Changer de projet', 'Change project'))
    change.addEventListener('click', () => {
      state.chosen = null
      state.inputs = { ...state.inputs, humanDays: null }
      state.openStep = 1
      invalidate()
      requestAnimationFrame(() => nodes.step1.scrollIntoView({
        block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth',
      }))
    })
    nodes.answer.appendChild(el('div', { class: 'step-head' },
      el('span', { class: 'step-num' }, '03'),
      el('h2', { tabindex: '-1' }, t('Le résultat', 'The answer')),
      change))
    // The volume moves with the model too: measured on our nine tasks, the models
    // that are not Claude spent a fraction of its tokens. So it is a span, like the price.
    const volumes = picks.map((pick) => pick.quote.tokens)
    const claude = current.tokens.in + current.tokens.out + current.tokens.cw + current.tokens.cr
    const fewest = volumes.length > 0 ? Math.min(...volumes) : claude
    const most = volumes.length > 0 ? Math.max(...volumes) : claude
    // Plus de montant unique en tête. Il a porté trois versions — le modèle recommandé,
    // puis celui du corpus — et aucune ne tenait : en abonnement il annonçait « 389 € par
    // mois » qu'aucun forfait ne pratique, et il nommait un modèle qu'aucune des trois
    // cartes ne propose. Le lecteur a la fourchette juste en dessous, et les cartes en
    // dessous encore (Armand, 21/09/2026).
    nodes.answer.appendChild(el('p', { class: 'answer-lead' },
      el('b', { class: 'answer-job' }, capitalise(jobDescription(current, state.inputs)))))

    const planOf = (pick: Pick): number | undefined => pick.quote.plan?.plan.eur_per_month
    const dearest = picks.length > 0
      ? picks.reduce((worst, pick) => (pick.costUsd > worst.costUsd ? pick : worst)) : null
    const cheapest = picks.length > 0
      ? picks.reduce((best, pick) => (pick.costUsd < best.costUsd ? pick : best)) : null
    const saidLow = monthly && cheapest ? planMoney(low, planOf(cheapest)) : money(low)
    const saidHigh = monthly && dearest ? planMoney(high, planOf(dearest)) : money(high)

    nodes.answer.appendChild(el('p', { class: 'note full' },
      ...(tokens(fewest) === tokens(most)
        ? [t('Environ ', 'About '), tokens(most), tokenUnit(most)]
        : [t('De ', 'From '), tokens(fewest), t(' à ', ' to '), tokens(most), tokenUnit(most)]),
      // Au mois, les bornes sont des tarifs affichés par les éditeurs, pas des montants
      // convertis : la ligne doit dire la même chose que les cartes.
      ...(saidLow === saidHigh
        // When the quality caps out, the three cards name one model — and « entre 0,02 € et
        // 0,02 € » reads as a bug. The token line above already guards the same way.
        ? [monthly
          ? t('. Un seul forfait est en jeu : ', '. One plan is in play: ')
          : t('. Un seul prix sur la grille : ', '. One price on the rate card: '),
        saidHigh, monthly ? t(' par mois.', ' per month.') : '.']
        : [monthly
          ? t('. Selon le modèle, l’abonnement va de ', '. Depending on the model, the plan runs from ')
          : t('. Selon le modèle, de ', '. Depending on the model, from '),
        saidLow, t(' à ', ' to '), saidHigh,
        monthly ? t(' par mois.', ' per month.') : '.'])))

    if (recommendations && picks.length > 0) {
      // No gloss on the /100 here: the notes at the foot of the page already say
      // where it comes from, and saying it twice on one screen is the defect this
      // project keeps finding in its own pages.
      const capSaid = capSentenceFr(recommendations)
      if (capSaid) nodes.answer.appendChild(el('p', { class: 'note full' }, capSaid))
      nodes.answer.appendChild(picksRow(current))
      // Le vrai risque d'un forfait n'est pas de ne pas pouvoir : c'est d'attendre. Dit une
      // fois sous les cartes plutôt qu'en avertissement sur chacune (Armand, 21/09/2026).
      if (monthly) {
        nodes.answer.appendChild(el('p', { class: 'note full' }, t(
          'Le forfait choisi ne change pas ce que l’agent sait faire, mais ses quotas '
          + 'peuvent l’arrêter en route : sur un gros travail, il faut parfois attendre la '
          + 'fenêtre suivante. Un forfait plus petit ne rend rien impossible, il étale le '
          + 'travail dans le temps.',
          'The plan you pick does not change what the agent can do, but its quotas can stop '
          + 'it along the way: on a big job you sometimes wait for the next window. A '
          + 'smaller plan makes nothing impossible, it spreads the work over time.')))
      }
      const cheaper = recommendations.cheaperUnscored
      if (cheaper) {
        nodes.answer.appendChild(el('p', { class: 'note full' },
          el('b', {}, cheaper.model.label),
          monthly
            ? t(' est accessible pour ', ' is available for ')
            : t(' ferait le même travail pour ', ' would do the same work for '),
          el('b', {}, money(cheaper.costUsd)),
          monthly ? t(' par mois — ', ' per month — ') : ' — ',
          el('b', {}, factor(cheaper.ratio)),
          t(' moins cher que le moins cher ci-dessus — mais aucun classement ne l’a testé '
            + 'sur ce type de travail, donc on ne le recommande pas. Ce n’est pas un avis '
            + 'sur sa qualité : c’est un trou dans les mesures.',
          ' cheaper than the cheapest above — but no ranking has tested it on this kind of '
            + 'work, so we do not recommend it. That is not a verdict on its quality: it is '
            + 'a hole in the measurements.')))
      }
    } else {
      nodes.answer.appendChild(el('p', { class: 'empty' }, t(
        'Aucun modèle de la grille n’a de note publiée sur ce type de travail. On '
        + 'ne recommande rien plutôt que de recommander au hasard.',
        'No model on the rate card has a published score for this kind of work. We would '
        + 'rather recommend nothing than recommend at random.')))
    }

    nodes.answer.appendChild(modelsSheet(current))

    // Re-triggering the animation needs a forced reflow, or it only ever plays
    // once for the life of the node.
    nodes.answer.classList.remove('is-fresh')
    void nodes.answer.offsetWidth
    nodes.answer.classList.add('is-fresh')
  }

  /** "2 millions de jetons" but "93 829 jetons": the "de" belongs to the word million. */
  function tokenUnit(value: number): string {
    return t(value >= 1e6 ? ' de jetons' : ' jetons', ' tokens')
  }

  function capitalise(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  function picksRow(current: ItemEstimate): HTMLElement {
    const recommendations = current.recommendations!
    const monthly = recommendations.billing === 'subscription'
    // Une carte par MODÈLE, pas par question. « Le moins cher » et « qualité / prix »
    // désignent souvent le même modèle — sur le code, le moins cher est aussi le mieux
    // noté de tout ce qui est abordable — et deux cartes identiques côte à côte se
    // lisaient comme un bug (Armand, 21/09/2026).
    const groups: { picks: Pick[] }[] = []
    for (const pick of recommendations.picks) {
      const found = groups.find((group) => group.picks[0]!.model.id === pick.model.id)
      if (found) found.picks.push(pick)
      else groups.push({ picks: [pick] })
    }
    return el('div', { class: 'picks' }, ...groups.map(({ picks: group }) => {
      // Le rôle qui porte la carte : celui qu'on recommande d'abord.
      const lead = group.find((pick) => pick.kind === 'value') ?? group[0]!
      return el('div', { class: `pick pick-${lead.kind}` },
        el('span', { class: 'pick-label' }, group.map((pick) => pick.label_fr).join(' · ')),
        // La note monte en haut à droite : en petit sous le prix, elle passait inaperçue
        // alors que c'est l'autre moitié de la décision (Armand, 21/09/2026).
        el('span', { class: 'pick-score' },
          el('b', {}, String(scorePoints(lead.score))),
          el('small', {}, '/100')),
        // The only anchor in the block for a reader who will never know what
        // "claude-fable-5.1" is.
        el('span', { class: 'pick-org' }, lead.model.provider.toUpperCase()),
        el('b', {}, lead.model.label),
        // En abonnement, un seul prix ne dit rien : le modèle est servi par plusieurs
        // forfaits, et c'est la fourchette qui situe le lecteur. Les tarifs montrés sont
        // ceux que l'éditeur affiche, pas leur conversion (Armand, 21/09/2026).
        monthly
          ? planRange(lead.model.id)
          : el('span', { class: 'pick-figure' }, money(lead.costUsd)),
        monthly && lead.quote.plan ? planLink(lead.quote.plan.plan) : null,
        // Ce qu'est le modèle, pas pourquoi l'algorithme l'a retenu : « à 8 points de la
        // meilleure note » est une phrase de mathématicien, et le rôle est déjà écrit en
        // tête de carte (Armand, 21/09/2026).
        el('p', { class: 'note' }, modelAbout(lead.model.id) ?? lead.reason_fr),
        pickFacts(lead, current),
        group.some((pick) => pick.kind === 'value')
          ? el('span', { class: 'pick-flag' }, t('Recommandé', 'Recommended'))
          : null)
    }))
  }

  /** Ce qu'il en coûte au mois : combien de forfaits donnent accès à ce modèle, et entre
   *  quels prix. Un montant seul laissait croire qu'il n'y avait qu'une porte d'entrée. */
  function planRange(modelId: string): HTMLElement {
    const plans = plansFor(modelId)
    const low = plans[0]
    const high = plans[plans.length - 1]
    if (!low || !high) {
      return el('span', { class: 'pick-figure' }, '—',
        el('span', { class: 'pick-unit' }, t(' aucun abonnement', ' no subscription')))
    }
    const lowSaid = planMoney(low.usd_per_month, low.eur_per_month)
    const highSaid = planMoney(high.usd_per_month, high.eur_per_month)
    return el('span', { class: 'pick-figure' },
      low.id === high.id ? lowSaid : `${lowSaid} ${t('à', 'to')} ${highSaid}`,
      el('span', { class: 'pick-unit' }, t(' /mois', ' /month')),
      el('small', {}, plans.length > 1
        ? t(`${plans.length} abonnements donnent accès à ce modèle`,
          `${plans.length} subscriptions give access to this model`)
        : t('un seul abonnement donne accès à ce modèle',
          'one subscription gives access to this model')))
  }

  /** Le forfait de la carte, et le lien vers la page tarifaire de l'éditeur : c'est là
   *  que le lecteur voit les paliers, les quotas et ce qu'il paiera vraiment. */
  function planLink(plan: Plan): HTMLElement {
    return el('a', {
      class: 'pick-plan', href: plan.url, target: '_blank', rel: 'noopener',
    }, `${plan.vendor_fr} · ${plan.name_fr}`,
    el('span', { class: 'pick-plan-more' }, t(' — voir les tarifs', ' — see the rates')))
  }

  /** Ce qui manque à ce modèle, s'il manque quelque chose. Rien d'autre : le plafond du
   *  forfait et la consommation non vérifiée ont été retirés le 21/09/2026 — un lecteur
   *  qui choisit un modèle n'en fait rien, et la page dit sous les cartes ce que les
   *  quotas changent vraiment. */
  function pickFacts(pick: Pick, current: ItemEstimate): HTMLElement | null {
    const line = reliabilityLine(pick.quote.reliability, current.item.capPoints === null
      ? null : automationBenchFr(current.item.domain, pick.quote.model.id))
    return line ? el('ul', { class: 'pick-facts' }, line) : null
  }

  function reliabilityLine(
    record: Reliability | null, bench: ReturnType<typeof automationBenchFr>,
  ): HTMLElement | null {
    // Seuls les manques se disent. Qu'un modèle soit allé au bout est la normale, pas
    // une nouvelle — et le lecteur n'a pas à compter nos essais.
    if (!record && bench) return bench.warn ? el('li', { class: 'warn' }, bench.text) : null
    // Ne rien dire d'un modèle qu'on n'a pas vu à l'œuvre : « pas vérifié » se lisait
    // comme un défaut, alors que n'importe quel modèle finit un projet tant qu'on paie.
    if (!record) return null
    if (record.finished < record.attempted) {
      return el('li', { class: 'warn' }, t(
        'Il lui est arrivé de ne pas aller au bout, sur ce type de travail.',
        'It has been known not to finish, on this kind of work.'))
    }
    return null
  }

  /**
   * The full grid, on demand.
   *
   * Seventeen rows inline pushed everything below them off the screen for what is
   * reference material: the answer is the three cards. A native <dialog> gets the
   * focus trap, Escape and the backdrop for free — no library, and it is a bottom
   * sheet on a phone where a centred modal reads as a mistake.
   */
  function modelsSheet(current: ItemEstimate): HTMLElement {
    const sheet = el('dialog', {
      class: 'sheet',
      'aria-label': t(`Les ${current.quotes.length} modèles comparés`,
        `The ${current.quotes.length} models compared`),
    }) as HTMLDialogElement

    const close = el('button', {
      class: 'sheet-close', type: 'button', 'aria-label': t('Fermer', 'Close'),
    }, '×')
    close.addEventListener('click', () => sheet.close())

    sheet.append(
      el('div', { class: 'sheet-head' },
        el('div', {},
          el('b', {}, t(`Les ${current.quotes.length} modèles comparés`,
            `The ${current.quotes.length} models compared`)),
          el('span', { class: 'note' },
            t('Prix pour ', 'Priced for '), jobDescription(current, state.inputs), '.')),
        close),
      el('div', { class: 'sheet-body' }, modelsTable(current)))

    // A click on the backdrop lands on the dialog element itself, never on its
    // content — so the target test is the whole implementation.
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) sheet.close()
    })
    // showModal() is supposed to handle Escape on its own, and I could not
    // confirm it through the automated browser. Wiring it explicitly costs three
    // lines and removes the doubt; closing twice is a no-op.
    sheet.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); sheet.close() }
    })

    const open = el('button', { class: 'linkish sheet-open', type: 'button' },
      t(`Voir les ${current.quotes.length} modèles comparés`,
        `See the ${current.quotes.length} models compared`))
    open.addEventListener('click', () => sheet.showModal())

    return el('p', { class: 'sheet-trigger' }, open, sheet)
  }

  function modelsTable(current: ItemEstimate): HTMLElement {
    if (state.inputs.billing === 'subscription') return monthlyTable(current)
    const baseline = current.recommendations?.picks
      .find((pick) => pick.kind === 'cheapest')?.costUsd
      ?? [...current.quotes].sort((a, b) => a.costUsd - b.costUsd)[0]?.costUsd
    return table(
      [{ label: '#', num: true }, { label: t('modèle', 'model') },
        { label: t('note', 'score'), num: true },
        { label: t('coût', 'cost'), num: true },
        { label: t('écart au moins cher', 'times the cheapest'), num: true }, { label: '' }],
      current.quotes.map((quote) => ({
        className: [
          quote.picks.length > 0 ? 'highlight' : null,
          quote.score === null ? 'muted' : null,
        ].filter(Boolean).join(' ') || undefined,
        cells: [
          el('span', { class: 'rank' }, quote.rank > 0 ? String(quote.rank) : '—'),
          quote.model.label,
          quote.score === null
            ? el('span', { class: 'tag warn' },
              quote.generalist === null
                ? t('non noté', 'unscored')
                : t(`non noté ici · ${scorePoints(quote.generalist)} en généraliste`,
                  `unscored here · ${scorePoints(quote.generalist)} on the general index`))
            : el('span', { class: 'score' }, String(scorePoints(quote.score))),
          money(quote.costUsd),
          baseline && baseline > 0 ? factor(quote.costUsd / baseline) : '—',
          quote.picks.length > 0 ? el('span', { class: 'tag src' }, pickTagsFr(quote)) : '',
        ],
      })), 'models-table')
  }

  /** Same rows, priced per month: a model with no plan stays listed, and says why. */
  function monthlyTable(current: ItemEstimate): HTMLElement {
    return table(
      [{ label: '#', num: true }, { label: t('modèle', 'model') },
        { label: t('note', 'score'), num: true },
        { label: t('par mois', 'per month'), num: true },
        { label: t('forfait', 'plan') }, { label: '' }],
      current.quotes.map((quote) => ({
        className: [
          quote.picks.length > 0 ? 'highlight' : null,
          quote.score === null || quote.billedUsd === null ? 'muted' : null,
        ].filter(Boolean).join(' ') || undefined,
        cells: [
          el('span', { class: 'rank' }, quote.rank > 0 ? String(quote.rank) : '—'),
          quote.model.label,
          quote.score === null
            ? el('span', { class: 'tag warn' }, t('non noté', 'unscored'))
            : el('span', { class: 'score' }, String(scorePoints(quote.score))),
          quote.billedUsd === null ? '—' : money(quote.billedUsd),
          quote.plan
            ? el('span', {}, `${quote.plan.plan.vendor_fr} · ${quote.plan.plan.name_fr}`,
              quote.plan.fitsMonth === false
                ? el('span', { class: 'tag warn' },
                  t(' ne tient pas dans un mois', ' does not fit in one month')) : null)
            : el('span', { class: 'tag warn' }, t('à l’usage seulement', 'pay as you go only')),
          quote.picks.length > 0 ? el('span', { class: 'tag src' }, pickTagsFr(quote)) : '',
        ],
      })), 'models-table monthly')
  }

  /** Every plan we list, as the vendor publishes it: nothing converted, everything dated. */
  // ------------------------------------------------------------------- human

  /** Our own order of magnitude for the job on screen, in person-days. */
  function defaultHumanDays(): number | null {
    const scale = HUMAN_DAYS[state.chosen ?? '']
    if (!scale) return null
    return scale[Math.min(scale.length - 1, Math.round(state.inputs.ampleur * 2))] ?? null
  }

  function drawHuman(): void {
    const current = estimate()
    clear(nodes.human)
    if (!current || !state.answered) return

    // Pre-filled with our figure, and replaced by the reader's as soon as they type one.
    const hint = defaultHumanDays() ?? undefined
    const days = el('input', {
      type: 'number', min: '0', step: '1',
      value: state.inputs.humanDays === null ? '' : String(state.inputs.humanDays),
      placeholder: hint === undefined
        ? t('ex. 20', 'e.g. 20')
        : t(`environ ${count(hint, hint < 1 ? 2 : 0)}`, `about ${count(hint, hint < 1 ? 2 : 0)}`),
    }) as HTMLInputElement
    days.addEventListener('change', () => {
      // Vidé, le champ redevient le nôtre et se remet à suivre le type choisi.
      state.humanDaysOwn = days.value !== ''
      state.inputs = {
        ...state.inputs,
        humanDays: days.value ? Number(days.value) : defaultHumanDays(),
      }
      drawHuman()
    })

    const rate = el('input', {
      type: 'number', min: '0', step: '50',
      value: String(Math.round(fromUsd(state.inputs.dailyRateUsd))),
    }) as HTMLInputElement
    rate.addEventListener('change', () => {
      const next = toUsd(Number(rate.value) || 0)
      state.inputs = { ...state.inputs, dailyRateUsd: next }
      remember(RATE_KEY, String(next))
      drawHuman()
    })

    // The AI side is the figure the cards stand on: per use the estimate, per month the
    // recommended model's plan. Computed here rather than in the engine, which knows
    // nothing of what the page recommends.
    const monthly = state.inputs.billing === 'subscription'
    const aiUsd = headline(current)
    const plan = pinnedQuote(current)?.plan?.plan
    const saving = current.humanCostUsd === null || aiUsd === null
      ? null : current.humanCostUsd - aiUsd
    const ratioHuman = current.humanCostUsd === null || aiUsd === null || aiUsd <= 0
      ? null : current.humanCostUsd / aiUsd
    const verdict = current.humanCostUsd === null || aiUsd === null ? null
      : el('div', { class: 'human' },
        el('div', { class: 'stat' },
          el('small', {}, t('en régie', 'with people')),
          money(current.humanCostUsd),
          // Une automatisation vaut un dixième de journée : arrondi à l'entier, la ligne
          // disait « 0 jour × 500 € ».
          el('em', {}, `${count(state.inputs.humanDays, (state.inputs.humanDays ?? 0) < 1 ? 1 : 0)} `
            + t(`jour${(state.inputs.humanDays ?? 0) > 1 ? 's' : ''}`,
              `day${(state.inputs.humanDays ?? 0) > 1 ? 's' : ''}`)
            + ` × ${count(Math.round(fromUsd(state.inputs.dailyRateUsd)))} ${currencySymbol()}`)),
        el('div', { class: 'stat' },
          el('small', {}, t('en confiant à l’IA', 'handed to the AI')),
          // Le prix d'un forfait est celui de l'éditeur, ici comme sur la carte : la
          // conversion affichait 7,36 € là où la carte disait 8 €.
          monthly && plan ? planMoney(aiUsd, plan.eur_per_month) : money(aiUsd),
          // Le modèle est déjà nommé sur les cartes : le redire poussait la ligne sur deux
          // rangs pour rien. Ne reste que ce que la colonne d'en face n'a pas — le temps
          // machine (Armand, 21/09/2026).
          el('em', {}, monthly && plan
            ? t(`un mois de ${plan.name_fr}`, `one month of ${plan.name_fr}`)
            : current.item.kind === 'automation'
              ? t('pour une exécution', 'for one run')
              : t(`${duration(current.workedHours * 3600)} de travail machine`,
                `${duration(current.workedHours * 3600)} of machine time`))),
        el('div', { class: 'stat' },
          el('small', {}, (saving ?? 0) > 0
            ? t('écart', 'difference') : t('surcoût', 'extra cost')),
          money(Math.abs(saving ?? 0)),
          el('em', {}, ratioHuman
            ? t(`soit ${factor(ratioHuman)} moins cher `
              + `${monthly ? 'abonnement compris' : 'côté API'}`,
            `that is ${factor(ratioHuman)} cheaper `
              + `${monthly ? 'subscription included' : 'on the API side'}`)
            : '')))

    nodes.human.appendChild(card(
      t('Et si c’était fait par quelqu’un ?', 'And if a person did it?'),
      t('Ce prix est une estimation qui ne contient ni le cadrage, ni vos '
        + 'allers-retours, ni la garantie. On ne compare ici que du temps de production.',
      'This price is an estimate that holds neither the scoping, nor your back and forth, '
        + 'nor any guarantee. All we compare here is production time.'),
      el('div', { class: 'controls' },
        el('label', { class: 'control' },
          el('span', {}, t('votre estimation (jours-homme)', 'your estimate (person-days)')),
          days),
        el('label', { class: 'control' },
          el('span', {}, t(`coût d’une journée (${currencySymbol()})`,
            `cost of one day (${currencySymbol()})`)), rate),
        // Sous les deux champs, pas sous le premier : c'est une phrase, et elle se lit
        // sur la largeur de la carte.
        el('p', { class: 'field-hint' }, t(
          'Une journée-homme = une personne, une journée de travail. Le chiffre affiché est '
          + 'notre ordre de grandeur : remplacez-le par le vôtre.',
          'One person-day = one person, one working day. The figure shown is our order of '
          + 'magnitude: replace it with yours.'))),
      verdict))
  }

  // ------------------------------------------------------------ contact/notes

  /**
   * The close: that piloting weighs, then the way to get help with it.
   *
   * Pushed harder when the job is big or stacks options — that is when an expert beside
   * the tool is worth the most, and when a figure read alone misleads the most.
   */
  function drawContact(): void {
    clear(nodes.contact)
    if (!state.answered) return
    const item = items.find((entry) => entry.id === state.chosen)
    const options = Object.values(state.inputs.modifiers).filter((value) => value === true).length
    // Size and options only mean something for a project: an automation must not inherit the
    // ones left over from a project viewed earlier.
    const heavy = item?.kind === 'project'
      && (item.id === 'produit_complet' || state.inputs.ampleur >= 1 || options >= 2)
    nodes.contact.appendChild(el('aside', { class: `contact${heavy ? ' contact-heavy' : ''}` },
      el('div', {},
        el('b', {}, t('Le modèle ne fait pas tout', 'The model is not the whole story')),
        // Un seul paragraphe : coupée en deux, la même idée se lisait comme deux réserves
        // empilées (Armand, 21/09/2026).
        el('p', {},
          t('Sur un même projet, le prix varie beaucoup selon qui le pilote et selon le '
            + 'nombre d’allers-retours : par les chiffres comme à l’usage, ce poids-là '
            + 'dépasse souvent le choix du modèle. ',
          'On one and the same project, the price varies a great deal with who steers it '
            + 'and with the number of round trips: by the figures as in practice, that '
            + 'weighs more often than the choice of model. ')
          + (heavy
            ? t('Sur un projet de cette ampleur, en parler avec quelqu’un qui en a mené '
              + 'coûte moins cher qu’une reprise, et donne un chiffre plus proche du réel.',
            'On a project of this size, talking it through with someone who has run one '
              + 'costs less than doing it twice, and gives a figure closer to the real one.')
            : t(CONTACT.pitch_fr, CONTACT.pitch_en)))),
      // The page state is not in the URL: sending the reader away would lose it.
      el('a', {
        class: 'primary', href: CONTACT.url, target: '_blank', rel: 'noopener',
      }, CONTACT.label_fr)))
  }

  function drawNotes(): void {
    const current = estimate()
    clear(nodes.notes)
    drawContact()
    if (!current || !state.answered) return
    const domain = current.recommendations?.domain ?? DOMAINS['coding_agent']
    const kept = [...(state.refresh?.stale ?? []), ...(state.refresh?.rejected ?? [])]

    const entries = [
      {
        label: t('D’où vient le prix', 'Where the price comes from'),
        body: [
          t('Nous exécutons ces travaux nous-mêmes et nous mesurons ce qu’ils consomment : '
            + 'le prix applique à ces mesures les tarifs publics des éditeurs, relevés au '
            + 'moment où vous cliquez. ',
          'We run this work ourselves and measure what it consumes: the price applies the '
            + 'vendors’ public rates to those measurements, read the moment you click. ')
          + currencyRateNote(),
          state.inputs.billing === 'subscription'
            ? t(' Au mois, c’est le forfait le moins cher qui donne accès au modèle.',
              ' Per month, it is the cheapest plan that gives access to the model.')
            : '',
        ],
      },
      {
        label: t('D’où vient la note', 'Where the score comes from'),
        body: [
          el('a', { href: domain.url, rel: 'noopener' }, domain.source_fr),
          t(`, ${domain.fetched_fr}. Nous complétons ce classement en faisant exécuter ces `
            + 'travaux aux modèles, pour vérifier que celui qu’on recommande les mène au '
            + 'bout.',
          `, ${domain.fetched_en}. We round that ranking out by having the models run this `
            + 'work, to check that the one we recommend sees it through.'),
        ],
      },
      {
        label: t('Ce que le cache change', 'What caching changes'),
        body: [
          t('Tous ces chiffres supposent que le modèle garde le contexte en mémoire '
            + 'd’un tour à l’autre. Sans ce cache, le même travail coûte environ ',
          'Every figure here assumes the model keeps the context in memory from one turn '
            + 'to the next. Without that cache, the same work costs about '),
          el('b', {}, t('8 fois', '8 times')),
          t(' plus.', ' more.'),
        ],
      },
      ...(kept.length > 0 ? [{
        label: t('Modèles non relevés', 'Models not read'),
        body: [t(`${kept.length} modèle${kept.length > 1 ? 's' : ''} sur `
          + `${state.grid.length} (${kept.join(', ')}) : prix du ${snapshotDay()}.`,
        `${kept.length} model${kept.length > 1 ? 's' : ''} of `
          + `${state.grid.length} (${kept.join(', ')}): price from ${snapshotDay()}.`)],
      }] : []),
    ]

    nodes.notes.appendChild(disclosure('guide-notes',
      t('Comment ce chiffre est calculé', 'How this figure is worked out'),
      { className: 'refine' },
      notesStrip(entries)))
  }

  /**
   * The question changed, so the answer is no longer an answer.
   *
   * Live-updating the figures when the reader picks another tile or moves the
   * size slider made the button decorative: numbers moved on their own, and
   * "Rechercher" became a formality one could skip. Clearing is the honest
   * behaviour — what is displayed always answers the question currently on
   * screen, or nothing is displayed.
   *
   * Deliberately does NOT redraw step 2: clear() there would destroy the range
   * input mid-drag.
   */
  function invalidate(): void {
    // The steps are redrawn in both branches: the accordion has to follow the
    // choice even the first time round, when there is no answer to clear yet.
    if (!state.answered) { drawSteps(); return }
    state.answered = false
    clear(nodes.answer)
    clear(nodes.human)
    clear(nodes.contact)
    clear(nodes.notes)
    nodes.answer.hidden = true
    drawSteps()
    announce(t('Le résultat a été effacé : relancez la recherche.',
      'The answer was cleared: run the search again.'))
  }

  function drawAll(): void {
    drawMasthead()
    drawSteps()
    drawAnswer()
    drawHuman()
    drawNotes()
    nodes.answer.hidden = !state.answered
  }

  drawStamp()
  drawAll()
}
